import React, { FC } from 'react'
import DocumentTitle from 'react-document-title'
import { Route } from 'react-router-dom'
import { makeStyles } from 'transfix-ui/core'
import { INavRouteGroup, INavRoute } from 'types'
import { QueryParamProvider } from 'use-query-params'
import { useImpersonation } from 'apps/user/components/UserDetails/User/hooks/useImpersonation'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { Router } from 'components/Router'
import { Header, Footer, SideNavWrapper } from 'components/layout'
import { Button } from 'transfix-ui'
import { generatePageTitle } from 'utils/generateAppDefaults'

export enum AppWrapNavVariants {
  side = 'side',
  top = 'top',
  none = 'none',
}

interface IAppWrapProps {
  appName: string
  title?: string
  noFooter?: boolean
  noHeader?: boolean
  routes?: INavRoute[]
  secondaryRoutes?: INavRoute[]
  homeRoute?: string
  variant?: AppWrapNavVariants
  navExpanded?: boolean
  logo?: React.ReactElement
  onLogout?: () => void
  onSupport?: () => void
  persistNavBar?: boolean
  /**
   * @default []
   */
  navRouteGroups?: INavRouteGroup[]
  /**
   * Enables new SideNav which utilizes `navRouteGroups`
   * Placed as a prop here so this can be enabled via
   * feature flag for each app
   * @default false
   */
  isNewSideNavEnabled?: boolean
}

const useAppWrapStyles = makeStyles({
  impersonationButton: {
    position: 'fixed',
    top: '1rem',
    left: 0,
    right: 0,
    zIndex: 1,
    width: '200px',
    margin: 'auto',
  },
})

export const AppWrap: FC<IAppWrapProps> = ({
  appName = '',
  variant = AppWrapNavVariants.top,
  children,
  navExpanded,
  title,
  noFooter,
  noHeader,
  routes: primaryRoutes,
  navRouteGroups = [],
  secondaryRoutes,
  homeRoute,
  logo,
  onLogout,
  onSupport,
  persistNavBar = false,
  isNewSideNavEnabled = false,
}) => {
  const styles = useAppWrapStyles()

  const { isImpersonating, stopImpersonation } = useImpersonation()

  return (
    <DocumentTitle title={title || generatePageTitle(appName)}>
      <Router basename={appName}>
        <QueryParamProvider ReactRouterRoute={Route}>
          <ErrorBoundary>
            {variant === AppWrapNavVariants.side && (
              <SideNavWrapper
                isNewSideNavEnabled={isNewSideNavEnabled}
                persistNavBar={persistNavBar}
                expanded={navExpanded}
                routes={primaryRoutes ?? []}
                secondaryRoutes={secondaryRoutes}
                homeRoute={homeRoute}
                logo={logo}
                onLogout={onLogout}
                navRouteGroups={navRouteGroups}
                onSupport={onSupport}>
                {children}
              </SideNavWrapper>
            )}

            {variant === AppWrapNavVariants.top && (
              <>
                {!noHeader && <Header routes={primaryRoutes} logo={logo} />}
                {children}
                {!noFooter && <Footer />}
              </>
            )}

            {variant === AppWrapNavVariants.none && (
              <div className='flex without-sidenav'>
                <main className='ma0 pa0 flex flex-column'>{children}</main>
              </div>
            )}

            {isImpersonating && (
              <Button className={styles.impersonationButton} onClick={stopImpersonation}>
                Stop Impersonating
              </Button>
            )}
          </ErrorBoundary>
        </QueryParamProvider>
      </Router>
    </DocumentTitle>
  )
}
